import type { FC } from '../lib/teact/teact';
import { useEffect, useLayoutEffect } from '../lib/teact/teact';
import { getActions, withGlobal } from '../global';

import type { GlobalState } from '../global/types';
import type { ThemeKey } from '../types';
import type { UiLoaderPage } from './common/UiLoader';

import { DARK_THEME_BG_COLOR, INACTIVE_MARKER, LIGHT_THEME_BG_COLOR, PAGE_TITLE, PAGE_TITLE_TAURI } from '../config';
import { forceMutation } from '../lib/fasterdom/stricterdom.ts';
import { selectTabState, selectTheme } from '../global/selectors';
import { selectCurrentMessageList } from '../global/selectors/messages';
import { IS_TAURI } from '../util/browser/globalEnvironment';
import { IS_INSTALL_PROMPT_SUPPORTED, PLATFORM_ENV } from '../util/browser/windowEnvironment';
import buildClassName from '../util/buildClassName';
import { setupBeforeInstallPrompt } from '../util/installPrompt';
import { ACCOUNT_SLOT, getAccountsInfo, getAccountSlotUrl } from '../util/multiaccount';
import { hasEncryptedSession } from '../util/passcode';
import { getInitialLocationHash, parseInitialLocationHash } from '../util/routing';
import { checkSessionLocked, hasStoredSession } from '../util/sessions';
import { updateSizes } from '../util/windowSize';
import { LeftColumnContent } from '../types';
import { initTelegramAuth } from '../util/telegramAuth';
import { apiRequest } from '../api/apiRequest';

import useTauriDrag from '../hooks/tauri/useTauriDrag';
import useAppLayout from '../hooks/useAppLayout';
import usePrevious from '../hooks/usePrevious';
import { useSignalEffect } from '../hooks/useSignalEffect.ts';
import { getIsInBackground } from '../hooks/window/useBackgroundMode.ts';

// import Test from './test/TestLocale';
import Auth from './auth/Auth';
import MobileNavBar from './common/MobileNavBar';
import UiLoader from './common/UiLoader';
import WebView from './common/WebView';
import AppInactive from './main/AppInactive';
import LockScreen from './main/LockScreen.async';
import Main from './main/Main.async';
import Transition from './ui/Transition';

import styles from './App.module.scss';

type StateProps = {
  authState: GlobalState['authState'];
  isScreenLocked?: boolean;
  hasPasscode?: boolean;
  inactiveReason?: 'auth' | 'otherClient';
  hasWebAuthTokenFailed?: boolean;
  isTestServer?: boolean;
  theme: ThemeKey;
  webView?: {
    url: string;
    title: string;
  };
  leftColumnContent?: LeftColumnContent;
  isChatOpen?: boolean;
};

enum AppScreens {
  auth,
  main,
  lock,
  inactive,
}

const TRANSITION_RENDER_COUNT = Object.keys(AppScreens).length / 2;
const ACTIVE_PAGE_TITLE = IS_TAURI ? PAGE_TITLE_TAURI : PAGE_TITLE;
const INACTIVE_PAGE_TITLE = `${ACTIVE_PAGE_TITLE} ${INACTIVE_MARKER}`;

const App: FC<StateProps> = ({
  authState,
  isScreenLocked,
  hasPasscode,
  inactiveReason,
  hasWebAuthTokenFailed,
  isTestServer,
  theme,
  webView,
  leftColumnContent,
  isChatOpen,
}) => {
  const { isMobile } = useAppLayout();
  const isMobileOs = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';
  const { closeWebView } = getActions();

  // Check if account is banned (localStorage only for initial display)
  // Real check happens on every API call via 403 interceptor
  // Check ban status from server on mount
  useEffect(() => {
    const checkBanStatus = async () => {
      const token = localStorage.getItem('telegram_token');
      if (!token) return;

      try {
        const data = await apiRequest('/api/users/ban-status', {
          method: 'GET',
        });
        
        if (data.isBanned) {
          // User is banned - set localStorage
          localStorage.setItem('account_banned', 'true');
          localStorage.setItem('ban_reason', data.banReason || 'Vi phạm điều khoản sử dụng');
        } else {
          // User is NOT banned - clear localStorage
          const wasBanned = localStorage.getItem('account_banned');
          if (wasBanned === 'true') {
            localStorage.removeItem('account_banned');
            localStorage.removeItem('ban_reason');
            window.location.reload(); // Reload to refresh UI
          }
        }
      } catch (err) {
        console.error('Failed to check ban status:', err);
      }
    };

    checkBanStatus();
  }, []);

  const isBanned = localStorage.getItem('account_banned') === 'true';
  const banReason = localStorage.getItem('ban_reason') || 'Vi phạm điều khoản sử dụng';

  // Security: Even if user removes localStorage flag, 
  // ALL API calls will still return 403 from server middleware
  // This is just UI layer, server-side is the real protection
  if (isBanned) {
    const handleRecheck = async () => {
      const token = localStorage.getItem('telegram_token');
      if (!token) return;

      try {
        const response = await fetch('/api/users/ban-status', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (!data.isBanned) {
            // User is no longer banned
            localStorage.removeItem('account_banned');
            localStorage.removeItem('ban_reason');
            window.location.reload();
          } else {
            alert('Tài khoản vẫn đang bị khóa');
          }
        }
      } catch (err) {
        console.error('Failed to recheck ban status:', err);
        alert('Không thể kiểm tra trạng thái');
      }
    };

    return (
      <div className="frozen-screen">
        <div className="frost-overlay"></div>
        <div className="ice-crystals">
          <div className="crystal crystal-1">❄</div>
          <div className="crystal crystal-2">❄</div>
          <div className="crystal crystal-3">❄</div>
          <div className="crystal crystal-4">❄</div>
          <div className="crystal crystal-5">❄</div>
          <div className="crystal crystal-6">❄</div>
          <div className="crystal crystal-7">❄</div>
          <div className="crystal crystal-8">❄</div>
        </div>
        <div className="frozen-content">
          <div className="freeze-icon">
            <svg width="100" height="100" viewBox="0 0 24 24" fill="none">
              <path d="M17 11H7V9C7 6.24 9.24 4 12 4C14.76 4 17 6.24 17 9M12 2C8.14 2 5 5.14 5 9V11C3.9 11 3 11.9 3 13V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V13C21 11.9 20.1 11 19 11M12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17Z" fill="#ADD8E6"></path>
            </svg>
          </div>
          <h1 className="freeze-title">TÀI KHOẢN BỊ ĐÓNG BĂNG</h1>
  
        </div>
      </div>
    );
  }

  // Determine active tab based on leftColumnContent
  const getActiveTab = (): 'contacts' | 'calls' | 'chats' | 'settings' | 'wallet' | 'gaito' | 'bookings' | 'reviews' | 'admin' => {
    switch (leftColumnContent) {
      case LeftColumnContent.Contacts:
        return 'contacts';
      case LeftColumnContent.Settings:
        return 'settings';
      case LeftColumnContent.Gaito:
        return 'gaito';
      case LeftColumnContent.Wallet:
        return 'wallet';
      case LeftColumnContent.Bookings:
        return 'bookings';
      case LeftColumnContent.Reviews:
        return 'reviews';
      case LeftColumnContent.Admin:
        return 'admin';
      case LeftColumnContent.ChatList:
      default:
        return 'chats';
    }
  };

  useEffect(() => {
    if (IS_INSTALL_PROMPT_SUPPORTED) {
      setupBeforeInstallPrompt();
    }

    // Initialize Telegram WebApp authentication
    initTelegramAuth().then((success) => {
      if (success) {
        console.log(' Telegram WebApp authentication initialized');
      } else {
        console.log('ℹ️ Running outside Telegram WebApp or authentication failed');
      }
    });
  }, []);

  useEffect(() => {
    const hash = getInitialLocationHash();
    // If there is no stored session on first slot, navigate to any other slot with stored session
    if (!hasStoredSession() && !ACCOUNT_SLOT && !hash) {
      const accounts = getAccountsInfo();
      Object.keys(accounts)
        .map(Number)
        .sort((a, b) => b - a)
        .forEach((key) => {
          const slot = Number(key);
          const account = accounts[slot];
          if (account) {
            const url = getAccountSlotUrl(slot);
            window.location.href = `${url}#${hash || 'login'}`;
          }
        });
    }

    // TODO[Passcode]: Remove when multiacc passcode is implemented
    const checkMultiaccPasscode = async () => {
      if (checkSessionLocked() && ACCOUNT_SLOT && await hasEncryptedSession()) {
        const url = getAccountSlotUrl(1);
        window.location.href = url;
      }
    };
    checkMultiaccPasscode();
  }, []);

  // Prevent drop on elements that do not accept it
  useEffect(() => {
    const body = document.body;
    const handleDrag = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      if (!(e.target as HTMLElement).dataset.dropzone) {
        e.dataTransfer.dropEffect = 'none';
      } else {
        e.dataTransfer.dropEffect = 'copy';
      }
    };
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    body.addEventListener('drop', handleDrop);
    body.addEventListener('dragover', handleDrag);
    body.addEventListener('dragenter', handleDrag);

    return () => {
      body.removeEventListener('drop', handleDrop);
      body.removeEventListener('dragover', handleDrag);
      body.removeEventListener('dragenter', handleDrag);
    };
  }, []);

  // return <Test />;

  let activeKey: AppScreens;
  let page: UiLoaderPage | undefined;

  if (inactiveReason) {
    activeKey = AppScreens.inactive;
  } else if (isScreenLocked) {
    page = 'lock';
    activeKey = AppScreens.lock;
  } else if (authState) {
    switch (authState) {
      case 'authorizationStateWaitPhoneNumber':
        page = 'authPhoneNumber';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitCode':
        page = 'authCode';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitPassword':
        page = 'authPassword';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitRegistration':
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateWaitQrCode':
        page = 'authQrCode';
        activeKey = AppScreens.auth;
        break;
      case 'authorizationStateClosed':
      case 'authorizationStateClosing':
      case 'authorizationStateLoggingOut':
      case 'authorizationStateReady':
        page = 'main';
        activeKey = AppScreens.main;
        break;
    }
  } else if (hasStoredSession()) {
    page = 'main';
    activeKey = AppScreens.main;
  } else if (hasPasscode) {
    activeKey = AppScreens.lock;
  } else {
    page = isMobileOs ? 'authPhoneNumber' : 'authQrCode';
    activeKey = AppScreens.auth;
  }

  if (activeKey !== AppScreens.lock
    && activeKey !== AppScreens.inactive
    && activeKey !== AppScreens.main
    && parseInitialLocationHash()?.tgWebAuthToken
    && !hasWebAuthTokenFailed) {
    page = 'main';
    activeKey = AppScreens.main;
  }

  useEffect(() => {
    updateSizes();
  }, []);

  useEffect(() => {
    if (inactiveReason) {
      document.title = INACTIVE_PAGE_TITLE;
    } else {
      document.title = ACTIVE_PAGE_TITLE;
    }
  }, [inactiveReason]);

  const prevActiveKey = usePrevious(activeKey);

  function renderContent() {
    switch (activeKey) {
      case AppScreens.auth:
        return <Auth />;
      case AppScreens.main:
        return <Main isMobile={isMobile} />;
      case AppScreens.lock:
        return <LockScreen isLocked={isScreenLocked} />;
      case AppScreens.inactive:
        return <AppInactive inactiveReason={inactiveReason!} />;
    }
  }

  useTauriDrag();

  useLayoutEffect(() => {
    document.body.classList.add(styles.bg);
  }, []);

  useLayoutEffect(() => {
    document.body.style.setProperty(
      '--theme-background-color',
      theme === 'dark' ? DARK_THEME_BG_COLOR : LIGHT_THEME_BG_COLOR,
    );
  }, [theme]);

  const getIsInBackgroundLocal = getIsInBackground;
  useSignalEffect(() => {
    // Mutation forced to avoid RAF throttling in background
    forceMutation(() => {
      document.body.classList.toggle('in-background', getIsInBackgroundLocal());
    }, document.body);
  }, [getIsInBackgroundLocal]);

  return (
    <>
      <UiLoader page={page} isMobile={isMobile}>
        <Transition
          name="fade"
          activeKey={activeKey}
          shouldCleanup
          className={buildClassName(
            'full-height',
            (activeKey === AppScreens.auth || prevActiveKey === AppScreens.auth) && 'is-auth',
          )}
          renderCount={TRANSITION_RENDER_COUNT}
        >
          {renderContent}
        </Transition>
        {activeKey === AppScreens.auth && isTestServer && <div className="test-server-badge">Test server</div>}
        {/* WebView temporarily disabled - ChatGPT/TikTok/Facebook block iframe embedding */}
        {/* {webView && (
          <WebView
            url={webView.url}
            title={webView.title}
            onClose={closeWebView}
          />
        )} */}
      </UiLoader>
      {isMobile && activeKey === AppScreens.main && !isChatOpen && <MobileNavBar activeTab={getActiveTab()} />}
    </>
  );
};

export default withGlobal(
  (global): Complete<StateProps> => {
    const tabState = selectTabState(global);
    const currentMessageList = selectCurrentMessageList(global);
    return {
      authState: global.authState,
      isScreenLocked: global.passcode?.isScreenLocked,
      hasPasscode: global.passcode?.hasPasscode,
      inactiveReason: tabState.inactiveReason,
      hasWebAuthTokenFailed: global.hasWebAuthTokenFailed || global.hasWebAuthTokenPasswordRequired,
      theme: selectTheme(global),
      isTestServer: global.config?.isTestServer,
      webView: tabState.webView,
      leftColumnContent: tabState.leftColumn?.contentKey,
      isChatOpen: Boolean(currentMessageList?.chatId),
    };
  },
)(App);
